import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Snackbar,
    Stack,
    TextField,
    Typography,
    LinearProgress,
    useTheme,
} from '@mui/material';
import { ContentCopy, Refresh, RestartAlt, Save, UploadFile } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGateApi } from '../Api/useGateApi';
import { useGateMainContext } from '../GateMainContext';
import {
    BrandingGateApiClient,
    IBrandingDTO,
} from '../Api/BrandingGateApiClient';
import { alpha, darken, getContrastRatio, type SxProps, type Theme } from '@mui/material/styles';
import { DEFAULT_GATE_PRIMARY, DEFAULT_GATE_SECONDARY } from '../Shared/CreateGateTheme';

const DEFAULT_PRIMARY = DEFAULT_GATE_PRIMARY;
const DEFAULT_SECONDARY = DEFAULT_GATE_SECONDARY;
const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;
const SLUG_REGEX = /^[a-z0-9-]{1,200}$/;
const MAX_FILE_BYTES = 1_500_000; // ~1.5MB raw → ~2MB base64
const MAX_BRAND_NAME_LENGTH = 64;
const MAX_SLUG_LENGTH = 200;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/x-icon', 'image/vnd.microsoft.icon'];
const DISALLOWED_URL_SCHEMES = ['javascript', 'vbscript', 'file'];

type BrandingState = {
    logoUrl: string;
    logoMarkUrl: string;
    faviconUrl: string;
    primaryColor: string;
    secondaryColor: string;
    brandName: string;
    urlSlug: string;
};

const EMPTY_STATE: BrandingState = {
    logoUrl: '',
    logoMarkUrl: '',
    faviconUrl: '',
    primaryColor: '',
    secondaryColor: '',
    brandName: '',
    urlSlug: '',
};

function toState(dto: IBrandingDTO | undefined | null): BrandingState {
    return {
        logoUrl: dto?.logoUrl ?? '',
        logoMarkUrl: dto?.logoMarkUrl ?? '',
        faviconUrl: dto?.faviconUrl ?? '',
        primaryColor: dto?.primaryColor ?? '',
        secondaryColor: dto?.secondaryColor ?? '',
        brandName: dto?.brandName ?? '',
        urlSlug: dto?.urlSlug ?? '',
    };
}

function isEqualState(a: BrandingState, b: BrandingState): boolean {
    return a.logoUrl === b.logoUrl
        && a.logoMarkUrl === b.logoMarkUrl
        && a.faviconUrl === b.faviconUrl
        && a.primaryColor === b.primaryColor
        && a.secondaryColor === b.secondaryColor
        && a.brandName === b.brandName
        && a.urlSlug === b.urlSlug;
}

// Mirror of the backend slug generation: strip diacritics, lowercase, and collapse any run
// of non-alphanumeric characters into a single hyphen.
function generateSlug(name: string): string {
    return name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_SLUG_LENGTH);
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
        reader.readAsDataURL(file);
    });
}

function containsControlCharacters(value: string): boolean {
    return Array.from(value).some(character => {
        const code = character.charCodeAt(0);
        return code < 0x20 || code === 0x7F;
    });
}

function getReadableStatusTextColor(tone: string, background: string, fallback: string): string {
    const candidates = [
        tone,
        darken(tone, 0.18),
        darken(tone, 0.32),
        darken(tone, 0.46),
    ];

    return candidates.find(candidate => getContrastRatio(candidate, background) >= 4.5) ?? fallback;
}

export default function OrganizationBranding() {
    const { t } = useTranslation();
    const theme = useTheme();
    const { refreshBranding } = useGateMainContext();
    const { authorizedApi } = useGateApi(BrandingGateApiClient);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [savedState, setSavedState] = useState<BrandingState>(EMPTY_STATE);
    const [currentState, setCurrentState] = useState<BrandingState>(EMPTY_STATE);
    const [errors, setErrors] = useState<Partial<Record<keyof BrandingState, string>>>({});
    const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({ open: false, severity: 'success', message: '' });

    const logoInputRef = useRef<HTMLInputElement>(null);
    const logoMarkInputRef = useRef<HTMLInputElement>(null);
    const faviconInputRef = useRef<HTMLInputElement>(null);

    const isDirty = useMemo(() => !isEqualState(savedState, currentState), [savedState, currentState]);
    const hasValidationErrors = useMemo(() => Object.values(errors).some(Boolean), [errors]);

    const loadBranding = useCallback(() => {
        setLoading(true);
        authorizedApi?.getTenantBranding()
            .then(res => {
                const next = toState(res.branding);
                setSavedState(next);
                setCurrentState(next);
                setErrors({});
            })
            .catch(() => {
                setSnack({ open: true, severity: 'error', message: t('Branding.LoadError') });
            })
            .finally(() => setLoading(false));
    }, [authorizedApi, t]);

    useEffect(() => {
        loadBranding();
    }, [loadBranding]);

    const validateField = useCallback((key: keyof BrandingState, value: string): string | undefined => {
        if (!value) return undefined;
        if (key === 'primaryColor' || key === 'secondaryColor') {
            return HEX_REGEX.test(value) ? undefined : t('Branding.Errors.InvalidHex');
        }
        if (key === 'brandName') {
            if (value.length > MAX_BRAND_NAME_LENGTH) return t('Branding.Errors.BrandNameTooLong');
            if (containsControlCharacters(value)) return t('Branding.Errors.BrandNameInvalid');
            return undefined;
        }
        if (key === 'urlSlug') {
            if (value.length > MAX_SLUG_LENGTH) return t('Branding.Errors.SlugTooLong', 'Slug is too long');
            return SLUG_REGEX.test(value) ? undefined : t('Branding.Errors.InvalidSlug', 'Only lowercase letters, digits and hyphens');
        }
        const lower = value.toLowerCase();
        if (DISALLOWED_URL_SCHEMES.some(scheme => lower.startsWith(`${scheme}:`))) {
            return t('Branding.Errors.DisallowedScheme');
        }
        if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('/') || lower.startsWith('data:image/')) {
            return undefined;
        }
        return t('Branding.Errors.InvalidUrl');
    }, [t]);

    const setField = useCallback((key: keyof BrandingState, value: string) => {
        setCurrentState(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: validateField(key, value) }));
    }, [validateField]);

    const handleFile = useCallback(async (key: 'logoUrl' | 'logoMarkUrl' | 'faviconUrl', file: File | undefined) => {
        if (!file) return;
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            setSnack({ open: true, severity: 'error', message: t('Branding.Errors.UnsupportedFileType') });
            return;
        }
        if (file.size > MAX_FILE_BYTES) {
            setSnack({ open: true, severity: 'error', message: t('Branding.Errors.FileTooLarge') });
            return;
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            setField(key, dataUrl);
        } catch {
            setSnack({ open: true, severity: 'error', message: t('Branding.Errors.FileReadFailed') });
        }
    }, [setField, t]);

    const handleSave = useCallback(() => {
        // Re-validate everything on submit
        const nextErrors: Partial<Record<keyof BrandingState, string>> = {};
        (Object.keys(currentState) as (keyof BrandingState)[]).forEach(k => {
            const err = validateField(k, currentState[k]);
            if (err) nextErrors[k] = err;
        });
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            setSnack({ open: true, severity: 'error', message: t('Branding.Errors.FixBeforeSave') });
            return;
        }

        setSaving(true);
        authorizedApi?.saveTenantBranding({
            logoUrl: currentState.logoUrl || null,
            logoMarkUrl: currentState.logoMarkUrl || null,
            faviconUrl: currentState.faviconUrl || null,
            primaryColor: currentState.primaryColor || null,
            secondaryColor: currentState.secondaryColor || null,
            brandName: currentState.brandName.trim() || null,
            urlSlug: currentState.urlSlug.trim() || null,
        })
            .then(res => {
                const next = toState(res.branding);
                setSavedState(next);
                setCurrentState(next);
                refreshBranding();
                setSnack({ open: true, severity: 'success', message: t('Branding.SaveSuccess') });
            })
            .catch((err: Error & { code?: string }) => {
                if (err.code === 'slug_taken') {
                    const message = t('Branding.Errors.SlugTaken', 'This login URL is already used by another organization. Choose a different slug.');
                    setErrors(prev => ({ ...prev, urlSlug: t('Branding.Errors.SlugTakenShort', 'Already taken') }));
                    setSnack({ open: true, severity: 'error', message });
                } else {
                    setSnack({ open: true, severity: 'error', message: err.message || t('Branding.SaveError') });
                }
            })
            .finally(() => setSaving(false));
    }, [authorizedApi, currentState, refreshBranding, t, validateField]);

    const handleResetForm = useCallback(() => {
        setCurrentState(savedState);
        setErrors({});
    }, [savedState]);

    const handleResetToDefaults = useCallback(() => {
        setResetting(true);
        authorizedApi?.resetTenantBranding()
            .then(() => {
                setSavedState(EMPTY_STATE);
                setCurrentState(EMPTY_STATE);
                setErrors({});
                refreshBranding();
                setSnack({ open: true, severity: 'success', message: t('Branding.ResetSuccess') });
            })
            .catch((err: Error) => {
                setSnack({ open: true, severity: 'error', message: err.message || t('Branding.ResetError') });
            })
            .finally(() => setResetting(false));
    }, [authorizedApi, refreshBranding, t]);

    const previewPrimary = HEX_REGEX.test(currentState.primaryColor) ? currentState.primaryColor : DEFAULT_PRIMARY;
    const previewSecondary = HEX_REGEX.test(currentState.secondaryColor) ? currentState.secondaryColor : DEFAULT_SECONDARY;
    const assistiveTextColor = alpha(theme.palette.text.primary, 0.72);
    const previewTrackColor = alpha(theme.palette.text.primary, 0.08);
    const fieldTextSx = useMemo<SxProps<Theme>>(() => ({
        '& .MuiInputLabel-root': {
            color: assistiveTextColor,
        },
        '& .MuiFormHelperText-root': {
            color: assistiveTextColor,
            opacity: 1,
        },
    }), [assistiveTextColor]);
    const getStatusBadgeSx = useCallback((tone: string): SxProps<Theme> => ({
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        lineHeight: 1.2,
        fontWeight: 700,
        border: '1px solid',
        borderColor: alpha(tone, 0.22),
        backgroundColor: alpha(tone, 0.1),
        color: getReadableStatusTextColor(tone, theme.palette.background.default, theme.palette.text.primary),
    }), [theme.palette.background.default, theme.palette.text.primary]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1100 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('Branding.Title')}</Typography>
                {!isDirty && (
                    <Typography variant="body2" component="span" sx={getStatusBadgeSx(theme.palette.success.main)}>{t('Branding.MatchesCurrent')}</Typography>
                )}
                {isDirty && (
                    <Typography variant="body2" component="span" sx={getStatusBadgeSx(theme.palette.warning.main)}>{t('Branding.UnsavedChanges')}</Typography>
                )}
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Left: preview panel */}
                <Box
                    sx={{
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        backgroundImage: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: `0 14px 32px ${theme.palette.action.disabledBackground}`,
                        p: 4,
                        minHeight: 360,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {currentState.logoMarkUrl
                            ? <Box component="img" src={currentState.logoMarkUrl} alt="" sx={{ width: 40, height: 40, objectFit: 'contain' }} />
                            : <Box sx={{ width: 40, height: 40, borderRadius: 1, backgroundColor: previewPrimary }} />}
                        {currentState.logoUrl
                            ? <Box component="img" src={currentState.logoUrl} alt="" sx={{ height: 32, objectFit: 'contain' }} />
                            : <Typography variant="h6" sx={{ fontWeight: 700 }}>{currentState.brandName.trim() || t('Branding.Preview.Brand')}</Typography>}
                    </Box>

                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {t('Branding.Preview.PrimaryAccent')}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={72}
                            sx={{
                                mt: 1,
                                mb: 2,
                                height: 8,
                                borderRadius: 999,
                                backgroundColor: previewTrackColor,
                                '& .MuiLinearProgress-bar': { backgroundColor: previewPrimary, borderRadius: 999 },
                            }}
                        />
                        <Typography variant="caption" sx={{ color: assistiveTextColor, textTransform: 'uppercase', letterSpacing: 1 }}>
                            {t('Branding.Preview.SecondaryAccent')}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={45}
                            sx={{
                                mt: 1,
                                height: 8,
                                borderRadius: 999,
                                backgroundColor: previewTrackColor,
                                '& .MuiLinearProgress-bar': { backgroundColor: previewSecondary, borderRadius: 999 },
                            }}
                        />
                    </Box>
                </Box>

                {/* Right: controls */}
                <Stack spacing={2}>
                    <TextField
                        label={t('Branding.BrandName')}
                        value={currentState.brandName}
                        onChange={e => setField('brandName', e.target.value)}
                        placeholder={t('Branding.BrandNameHelper')}
                        size="small"
                        fullWidth
                        error={!!errors.brandName}
                        helperText={errors.brandName ?? t('Branding.BrandNameHelper')}
                        sx={fieldTextSx}
                        slotProps={{ htmlInput: { maxLength: MAX_BRAND_NAME_LENGTH } }}
                    />
                    <BrandingAssetField
                        label={t('Branding.Logo')}
                        helper={t('Branding.LogoHelper')}
                        value={currentState.logoUrl}
                        onChange={v => setField('logoUrl', v)}
                        onPickFile={() => logoInputRef.current?.click()}
                        error={errors.logoUrl}
                        fieldSx={fieldTextSx}
                    />
                    <input ref={logoInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES.join(',')} hidden onChange={e => handleFile('logoUrl', e.target.files?.[0])} />

                    <BrandingAssetField
                        label={t('Branding.LogoMark')}
                        helper={t('Branding.LogoMarkHelper')}
                        value={currentState.logoMarkUrl}
                        onChange={v => setField('logoMarkUrl', v)}
                        onPickFile={() => logoMarkInputRef.current?.click()}
                        error={errors.logoMarkUrl}
                        fieldSx={fieldTextSx}
                    />
                    <input ref={logoMarkInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES.join(',')} hidden onChange={e => handleFile('logoMarkUrl', e.target.files?.[0])} />

                    <BrandingAssetField
                        label={t('Branding.Favicon')}
                        helper={t('Branding.FaviconHelper')}
                        value={currentState.faviconUrl}
                        onChange={v => setField('faviconUrl', v)}
                        onPickFile={() => faviconInputRef.current?.click()}
                        error={errors.faviconUrl}
                        fieldSx={fieldTextSx}
                    />
                    <input ref={faviconInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES.join(',')} hidden onChange={e => handleFile('faviconUrl', e.target.files?.[0])} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <ColorField
                            label={t('Branding.PrimaryColor')}
                            value={currentState.primaryColor}
                            placeholder={DEFAULT_PRIMARY}
                            onChange={v => setField('primaryColor', v)}
                            error={errors.primaryColor}
                            fieldSx={fieldTextSx}
                        />
                        <ColorField
                            label={t('Branding.SecondaryColor')}
                            value={currentState.secondaryColor}
                            placeholder={DEFAULT_SECONDARY}
                            onChange={v => setField('secondaryColor', v)}
                            error={errors.secondaryColor}
                            fieldSx={fieldTextSx}
                        />
                    </Box>
                </Stack>
            </Box>

            {/* Branded login URL: a tenant-specific slug that exposes /login/{slug} so the
                correct branding shows before the user signs in. */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('Branding.LoginUrlTitle', 'Branded login URL')}
                </Typography>
                <Typography variant="body2" sx={{ color: assistiveTextColor, mb: 1.5 }}>
                    {t('Branding.LoginUrlDescription', 'Share this link so users land on your branded login page.')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <TextField
                        label={t('Branding.UrlSlug', 'URL slug')}
                        value={currentState.urlSlug}
                        onChange={e => setField('urlSlug', e.target.value.trim().toLowerCase())}
                        placeholder="my-organization"
                        size="small"
                        fullWidth
                        error={!!errors.urlSlug}
                        helperText={errors.urlSlug ?? t('Branding.UrlSlugHelper', 'Only lowercase letters, digits and hyphens')}
                        sx={fieldTextSx}
                        slotProps={{ htmlInput: { maxLength: MAX_SLUG_LENGTH } }}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => setField('urlSlug', generateSlug(currentState.brandName))}
                        disabled={!currentState.brandName.trim()}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                        {t('Branding.GenerateSlug', 'Generate from name')}
                    </Button>
                </Stack>
                {currentState.urlSlug && !errors.urlSlug && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                        <TextField
                            value={`${window.location.origin}/login/${currentState.urlSlug}`}
                            size="small"
                            fullWidth
                            slotProps={{ input: { readOnly: true } }}
                            sx={fieldTextSx}
                        />
                        <IconButton
                            aria-label={t('Branding.CopyLoginUrl', 'Copy login URL')}
                            onClick={() => {
                                const url = `${window.location.origin}/login/${currentState.urlSlug}`;
                                navigator.clipboard?.writeText(url)
                                    .then(() => setSnack({ open: true, severity: 'success', message: t('Branding.LoginUrlCopied', 'Login URL copied') }))
                                    .catch(() => setSnack({ open: true, severity: 'error', message: t('Branding.LoginUrlCopyFailed', 'Could not copy URL') }));
                            }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Stack>
                )}
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }} justifyContent="flex-end">
                <Button
                    variant="text"
                    startIcon={<Refresh />}
                    onClick={handleResetForm}
                    disabled={!isDirty || saving || resetting}
                >
                    {t('Branding.ResetForm')}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartAlt />}
                    onClick={handleResetToDefaults}
                    disabled={saving || resetting}
                >
                    {resetting ? <CircularProgress size={18} /> : t('Branding.ResetToDefaults')}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={!isDirty || saving || resetting || hasValidationErrors}
                >
                    {saving ? <CircularProgress size={18} /> : t('Branding.Save')}
                </Button>
            </Stack>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

interface BrandingAssetFieldProps {
    label: string;
    helper: string;
    value: string;
    onChange: (v: string) => void;
    onPickFile: () => void;
    error?: string;
    fieldSx?: SxProps<Theme>;
}

function BrandingAssetField({ label, helper, value, onChange, onPickFile, error, fieldSx }: Readonly<BrandingAssetFieldProps>) {
    return (
        <TextField
            label={label}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={helper}
            size="small"
            fullWidth
            error={!!error}
            helperText={error ?? helper}
            sx={fieldSx}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={onPickFile} edge="end" aria-label="upload">
                                <UploadFile fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}

interface ColorFieldProps {
    label: string;
    value: string;
    placeholder: string;
    onChange: (v: string) => void;
    error?: string;
    fieldSx?: SxProps<Theme>;
}

function ColorField({ label, value, placeholder, onChange, error, fieldSx }: Readonly<ColorFieldProps>) {
    const isValid = !value || HEX_REGEX.test(value);
    const swatch = isValid && value ? value : placeholder;
    return (
        <TextField
            label={label}
            value={value}
            onChange={e => onChange(e.target.value.trim())}
            placeholder={placeholder}
            size="small"
            error={!!error}
            helperText={error}
            sx={fieldSx}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Box
                                component="label"
                                sx={{
                                    position: 'relative',
                                    width: 26,
                                    height: 26,
                                    borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.15)',
                                    backgroundColor: swatch,
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                }}
                            >
                                <input
                                    type="color"
                                    value={HEX_REGEX.test(value) ? value : placeholder}
                                    onChange={e => onChange(e.target.value.toUpperCase())}
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    aria-label={label}
                                />
                            </Box>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}
